import { FX, RULE_MAX_TICKET_EUR, RULE_REGIONS, RULE_SECTORS } from './data';
import type { Region, SampleDeal } from './data';
import { byClose, liveAt, type HomeRaise } from './live';

/** The rule built in "How it works", step 02. */
export interface Rule {
  regions: Region[];
  sectors: string[];
  /** Only raises whose minimum ticket is at most €500. */
  ticketCap: boolean;
}

export const DEFAULT_RULE: Rule = {
  regions: ['EU', 'UK'],
  sectors: ['SaaS', 'Fintech'],
  ticketCap: true,
};

/** How many raises the line under the count names. */
export const RULE_NAMES = 3;

/** “EU + UK · SaaS, Fintech · min ticket ≤ €500”, chips in their own order. */
export function ruleSentence(rule: Rule): string {
  const regions = RULE_REGIONS.filter((r) => rule.regions.includes(r));
  const sectors = RULE_SECTORS.filter((s) => rule.sectors.includes(s));
  const parts = [
    regions.length ? regions.join(' + ') : 'All regions',
    sectors.length ? sectors.join(', ') : 'All sectors',
  ];
  if (rule.ticketCap) parts.push(`min ticket ≤ €${RULE_MAX_TICKET_EUR}`);
  return `“${parts.join(' · ')}”`;
}

/**
 * A sample deal's minimum ticket in dollars, so tickets in any currency
 * compare: /get-started's preview, which replays the sample deals.
 */
export function ticketUsd(deal: SampleDeal): number {
  return deal.minTicket / (FX[deal.currency] ?? 1);
}

/**
 * Live raises that answer the chips alike, counted: the page sends these
 * rather than every live raise, so the script can match any rule the chips
 * make without the whole feed. `first` is the group's soonest-closing
 * raises, enough for the names line whichever groups a rule takes in.
 */
export interface RuleGroup {
  region: string;
  sectors: string[];
  ticketOk: boolean;
  count: number;
  /** [name, closes at], soonest first. */
  first: [string, number][];
}

/** The live raises at `now`, grouped by what the chips can ask of them. */
export function ruleGroups(
  raises: readonly HomeRaise[],
  now: number,
): RuleGroup[] {
  const groups = new Map<string, RuleGroup>();
  for (const r of liveAt(raises, now).sort(byClose)) {
    const key = `${r.region}|${r.sectors.join(',')}|${r.ticketOk}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        region: r.region,
        sectors: r.sectors,
        ticketOk: r.ticketOk,
        count: 0,
        first: [],
      };
      groups.set(key, group);
    }
    group.count++;
    if (group.first.length < RULE_NAMES) group.first.push([r.name, r.closesAt]);
  }
  return [...groups.values()];
}

export interface RuleMatch {
  count: number;
  /** The soonest-closing matches, at most RULE_NAMES. */
  names: string[];
}

/**
 * Live raises the rule would alert on today, as the real rules match them
 * (ruleMatches in src/alerts/alert.rules.ts, which filed each raise). An
 * empty region or sector list means every one, as the sentence says ("All
 * regions").
 */
export function matchRule(rule: Rule, groups: readonly RuleGroup[]): RuleMatch {
  const hits = groups.filter(
    (g) =>
      (rule.regions.length === 0 ||
        (rule.regions as string[]).includes(g.region)) &&
      (rule.sectors.length === 0 ||
        rule.sectors.some((s) => g.sectors.includes(s))) &&
      (!rule.ticketCap || g.ticketOk),
  );
  const names = hits
    .flatMap((g) => g.first)
    .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
    .slice(0, RULE_NAMES)
    .map(([name]) => name);
  return { count: hits.reduce((n, g) => n + g.count, 0), names };
}

/** "Acme · Bolt · Cove + 12 more". */
export function matchNames(match: RuleMatch): string {
  const more = match.count - match.names.length;
  return `${match.names.join(' · ')}${more > 0 ? ` + ${more} more` : ''}`;
}

/** "live raises match", after the count. */
export function matchNoun(count: number): string {
  return count === 1 ? 'live raise matches' : 'live raises match';
}
